import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Common and Widgets
import { UnifiedIcon } from '@/components/UnifiedIcon';

// Relative imports
import { EditorLinkPopover } from './editor/EditorLinkPopover.jsx';
import { createBaseEditorExtensions } from './editor/core/editorExtensions.js';
import './editor/editorStyles.css';

// ------------------------------------------------------
/**
 * @param {boolean} [isActive]
 * @returns {string}
 */
const getEditorButtonClassName = (isActive = false) =>
  `editor-buttons inline-flex h-9 w-9 items-center justify-center ${
    isActive
      ? 'is-active bg-violet-50 text-violet-700 ring-1 ring-violet-200 dark:bg-violet-500/20 dark:text-violet-200 dark:ring-violet-400/40'
      : ''
  }`;

const TOOLBAR_SECTION_CLASS =
  'inline-flex flex-wrap items-center gap-1 rounded-xl border border-gray-200 bg-white/95 p-1 shadow-sm shadow-gray-200/70 dark:border-gray-700 dark:bg-gray-900/95 dark:shadow-black/20';

const HEADING_LEVELS = [1, 2, 3, 4, 5, 6];

/**
 * @param {import('react').MouseEvent<HTMLButtonElement>} event
 * @param {() => void} command
 * @returns {void}
 */
const runEditorCommand = (event, command) => {
  event.preventDefault();
  command();
};

/**
 * @param {import('./editor').MenuBarProps} props
 * @returns {JSX.Element | null}
 */
export const MenuBar = ({ editor, setShowLinkPopover, showLinkPopover, t }) => {
  const [showHeadingDropdown, setShowHeadingDropdown] = useState(false);

  if (!editor) {
    return null;
  }

  /**
   * @param {import('./editor').EditorToolbarButtonConfig} button
   * @returns {JSX.Element}
   */
  const renderToolbarButton = ({
    disabled = false,
    iconName,
    isActive = false,
    labelKey,
    onClick,
    testId,
  }) => {
    const label = t(labelKey);

    return (
      <button
        aria-label={label}
        className={getEditorButtonClassName(isActive)}
        data-testid={testId}
        disabled={disabled}
        key={testId}
        onClick={(event) => {
          if (event.detail === 0) {
            onClick(event);
          }
        }}
        onMouseDown={onClick}
        title={label}
        type="button"
      >
        <UnifiedIcon
          className="h-4 w-4"
          data-testid={`${testId}-icon`}
          library="phosphor"
          name={iconName}
          size={18}
        />
      </button>
    );
  };

  /**
   * @param {string} testId
   * @param {JSX.Element | JSX.Element[]} children
   * @returns {JSX.Element}
   */
  const renderToolbarSection = (testId, children) => (
    <div className={TOOLBAR_SECTION_CLASS} data-testid={testId}>
      {children}
    </div>
  );

  const activeHeadingLevel =
    HEADING_LEVELS.find((level) => editor.isActive('heading', { level })) || null;

  /**
   * @returns {JSX.Element}
   */
  const renderHeadingDropdown = () => {
    const label = activeHeadingLevel ? `H${activeHeadingLevel}` : t('editor.toolbar.heading');
    const headingTitle = t('editor.toolbar.heading');

    return (
      <div className="relative" data-testid="widgets-editor-toolbar-heading-dropdown">
        <button
          aria-expanded={showHeadingDropdown}
          aria-label={headingTitle}
          className={`${getEditorButtonClassName(Boolean(activeHeadingLevel))} w-auto gap-1 px-2 font-medium`}
          data-testid="widgets-editor-toolbar-heading"
          onClick={(event) => {
            if (event.detail === 0) {
              event.preventDefault();
              setShowHeadingDropdown((current) => !current);
            }
          }}
          onMouseDown={(event) => {
            event.preventDefault();
            setShowHeadingDropdown((current) => !current);
          }}
          title={headingTitle}
          type="button"
        >
          <span className="min-w-6 text-xs">{label}</span>
          <UnifiedIcon
            className="h-3.5 w-3.5"
            data-testid="widgets-editor-toolbar-heading-caret-icon"
            library="phosphor"
            name="CaretDown"
            size={14}
          />
        </button>

        {showHeadingDropdown ? (
          <div
            className="absolute left-0 top-11 z-20 min-w-36 rounded-xl border border-gray-200 bg-white p-1 shadow-lg shadow-gray-200/80 dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/30"
            data-testid="widgets-editor-toolbar-heading-menu"
          >
            {HEADING_LEVELS.map((level) => {
              const optionLabel = t(`editor.toolbar.heading${level}`);

              return (
                <button
                  aria-label={optionLabel}
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800 ${
                    editor.isActive('heading', { level })
                      ? 'bg-violet-50 text-violet-700 dark:bg-violet-500/20 dark:text-violet-200'
                      : ''
                  }`}
                  data-testid={`widgets-editor-toolbar-heading-${level}`}
                  key={level}
                  onClick={(event) => {
                    if (event.detail === 0) {
                      runEditorCommand(event, () =>
                        editor.chain().focus().toggleHeading({ level }).run()
                      );
                      setShowHeadingDropdown(false);
                    }
                  }}
                  onMouseDown={(event) => {
                    runEditorCommand(event, () =>
                      editor.chain().focus().toggleHeading({ level }).run()
                    );
                    setShowHeadingDropdown(false);
                  }}
                  title={optionLabel}
                  type="button"
                >
                  <span>{optionLabel}</span>
                  <span className="text-xs text-gray-400 dark:text-gray-500">{`H${level}`}</span>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  };

  const isTableActive = editor.isActive('table');
  const isLinkActive = editor.isActive('link');

  /**
   * @returns {JSX.Element}
   */
  const renderLinkControl = () => {
    const linkTitle = t('editor.toolbar.link');

    return (
      <div className="relative" data-testid="widgets-editor-toolbar-link-dropdown">
        <button
          aria-expanded={showLinkPopover}
          aria-label={linkTitle}
          className={getEditorButtonClassName(isLinkActive)}
          data-testid="widgets-editor-toolbar-link"
          onClick={(event) => {
            if (event.detail === 0) {
              event.preventDefault();
              setShowHeadingDropdown(false);
              setShowLinkPopover((current) => !current);
            }
          }}
          onMouseDown={(event) => {
            event.preventDefault();
            setShowHeadingDropdown(false);
            setShowLinkPopover((current) => !current);
          }}
          title={linkTitle}
          type="button"
        >
          <UnifiedIcon
            className="h-4 w-4"
            data-testid="widgets-editor-toolbar-link-icon"
            library="phosphor"
            name="Link"
            size={18}
          />
        </button>
        <EditorLinkPopover
          editor={editor}
          isOpen={showLinkPopover}
          onClose={() => setShowLinkPopover(false)}
          t={t}
        />
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-2 px-2 pt-2" data-testid="widgets-editor-toolbar">
      <div
        className="flex flex-wrap items-center justify-start gap-3 border-b border-gray-200 pb-2 dark:border-gray-700"
        data-testid="widgets-editor-toolbar-primary-row"
      >
        {renderToolbarSection('widgets-editor-toolbar-formatting', [
          renderToolbarButton({
            disabled: !editor.can().chain().focus().toggleBold().run(),
            iconName: 'TextB',
            isActive: editor.isActive('bold'),
            labelKey: 'editor.toolbar.bold',
            onClick: (event) =>
              runEditorCommand(event, () => editor.chain().focus().toggleBold().run()),
            testId: 'widgets-editor-toolbar-bold',
          }),
          renderToolbarButton({
            disabled: !editor.can().chain().focus().toggleItalic().run(),
            iconName: 'TextItalic',
            isActive: editor.isActive('italic'),
            labelKey: 'editor.toolbar.italic',
            onClick: (event) =>
              runEditorCommand(event, () => editor.chain().focus().toggleItalic().run()),
            testId: 'widgets-editor-toolbar-italic',
          }),
          renderToolbarButton({
            disabled: !editor.can().chain().focus().toggleStrike().run(),
            iconName: 'TextStrikethrough',
            isActive: editor.isActive('strike'),
            labelKey: 'editor.toolbar.strike',
            onClick: (event) =>
              runEditorCommand(event, () => editor.chain().focus().toggleStrike().run()),
            testId: 'widgets-editor-toolbar-strike',
          }),
          renderToolbarButton({
            disabled: !editor.can().chain().focus().toggleCode().run(),
            iconName: 'Code',
            isActive: editor.isActive('code'),
            labelKey: 'editor.toolbar.code',
            onClick: (event) =>
              runEditorCommand(event, () => editor.chain().focus().toggleCode().run()),
            testId: 'widgets-editor-toolbar-code',
          }),
          renderToolbarButton({
            iconName: 'Eraser',
            labelKey: 'editor.toolbar.clearFormatting',
            onClick: (event) =>
              runEditorCommand(event, () => editor.chain().focus().unsetAllMarks().run()),
            testId: 'widgets-editor-toolbar-clear-formatting',
          }),
          renderLinkControl(),
        ])}

        {renderToolbarSection('widgets-editor-toolbar-blocks', [
          renderToolbarButton({
            iconName: 'Paragraph',
            labelKey: 'editor.toolbar.paragraph',
            onClick: (event) =>
              runEditorCommand(event, () => editor.chain().focus().setParagraph().run()),
            testId: 'widgets-editor-toolbar-paragraph',
          }),
          renderHeadingDropdown(),
          renderToolbarButton({
            iconName: 'ListBullets',
            isActive: editor.isActive('bulletList'),
            labelKey: 'editor.toolbar.bulletList',
            onClick: (event) =>
              runEditorCommand(event, () => editor.chain().focus().toggleBulletList().run()),
            testId: 'widgets-editor-toolbar-bullet-list',
          }),
          renderToolbarButton({
            iconName: 'ListNumbers',
            isActive: editor.isActive('orderedList'),
            labelKey: 'editor.toolbar.orderedList',
            onClick: (event) =>
              runEditorCommand(event, () => editor.chain().focus().toggleOrderedList().run()),
            testId: 'widgets-editor-toolbar-ordered-list',
          }),
          renderToolbarButton({
            iconName: 'CodeBlock',
            isActive: editor.isActive('codeBlock'),
            labelKey: 'editor.toolbar.codeBlock',
            onClick: (event) =>
              runEditorCommand(event, () => editor.chain().focus().toggleCodeBlock().run()),
            testId: 'widgets-editor-toolbar-code-block',
          }),
          renderToolbarButton({
            iconName: 'Quotes',
            isActive: editor.isActive('blockquote'),
            labelKey: 'editor.toolbar.blockquote',
            onClick: (event) =>
              runEditorCommand(event, () => editor.chain().focus().toggleBlockquote().run()),
            testId: 'widgets-editor-toolbar-blockquote',
          }),
          renderToolbarButton({
            iconName: 'Minus',
            labelKey: 'editor.toolbar.horizontalRule',
            onClick: (event) =>
              runEditorCommand(event, () => editor.chain().focus().setHorizontalRule().run()),
            testId: 'widgets-editor-toolbar-horizontal-rule',
          }),
        ])}

        <div
          aria-hidden="true"
          className="hidden"
          data-testid="widgets-editor-toolbar-table-separator"
        />

        {renderToolbarSection('widgets-editor-toolbar-table-create', [
          renderToolbarButton({
            iconName: 'Table',
            isActive: isTableActive,
            labelKey: 'editor.toolbar.insertTable',
            onClick: (event) =>
              runEditorCommand(event, () =>
                editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
              ),
            testId: 'widgets-editor-toolbar-insert-table',
          }),
        ])}

        <div
          aria-hidden="true"
          className="hidden"
          data-testid="widgets-editor-toolbar-history-separator"
        />

        {renderToolbarSection('widgets-editor-toolbar-history', [
          renderToolbarButton({
            disabled: !editor.can().chain().focus().undo().run(),
            iconName: 'ArrowCounterClockwise',
            labelKey: 'editor.toolbar.undo',
            onClick: (event) => runEditorCommand(event, () => editor.chain().focus().undo().run()),
            testId: 'widgets-editor-toolbar-undo',
          }),
          renderToolbarButton({
            disabled: !editor.can().chain().focus().redo().run(),
            iconName: 'ArrowClockwise',
            labelKey: 'editor.toolbar.redo',
            onClick: (event) => runEditorCommand(event, () => editor.chain().focus().redo().run()),
            testId: 'widgets-editor-toolbar-redo',
          }),
        ])}
      </div>

      {isTableActive ? (
        <div
          className="flex flex-wrap items-center justify-start gap-3 border-b border-gray-200 pb-2 dark:border-gray-700"
          data-testid="widgets-editor-toolbar-table-row"
        >
          {renderToolbarSection('widgets-editor-toolbar-table-structure', [
            renderToolbarButton({
              disabled: !editor.can().chain().focus().deleteTable().run(),
              iconName: 'Trash',
              labelKey: 'editor.toolbar.deleteTable',
              onClick: (event) =>
                runEditorCommand(event, () => editor.chain().focus().deleteTable().run()),
              testId: 'widgets-editor-toolbar-delete-table',
            }),
            renderToolbarButton({
              disabled: !editor.can().chain().focus().addColumnBefore().run(),
              iconName: 'ColumnsPlusLeft',
              labelKey: 'editor.toolbar.addColumnBefore',
              onClick: (event) =>
                runEditorCommand(event, () => editor.chain().focus().addColumnBefore().run()),
              testId: 'widgets-editor-toolbar-add-column-before',
            }),
            renderToolbarButton({
              disabled: !editor.can().chain().focus().addColumnAfter().run(),
              iconName: 'ColumnsPlusRight',
              labelKey: 'editor.toolbar.addColumnAfter',
              onClick: (event) =>
                runEditorCommand(event, () => editor.chain().focus().addColumnAfter().run()),
              testId: 'widgets-editor-toolbar-add-column-after',
            }),
            renderToolbarButton({
              disabled: !editor.can().chain().focus().deleteColumn().run(),
              iconName: 'Columns',
              labelKey: 'editor.toolbar.deleteColumn',
              onClick: (event) =>
                runEditorCommand(event, () => editor.chain().focus().deleteColumn().run()),
              testId: 'widgets-editor-toolbar-delete-column',
            }),
            renderToolbarButton({
              disabled: !editor.can().chain().focus().addRowBefore().run(),
              iconName: 'RowsPlusTop',
              labelKey: 'editor.toolbar.addRowBefore',
              onClick: (event) =>
                runEditorCommand(event, () => editor.chain().focus().addRowBefore().run()),
              testId: 'widgets-editor-toolbar-add-row-before',
            }),
            renderToolbarButton({
              disabled: !editor.can().chain().focus().addRowAfter().run(),
              iconName: 'RowsPlusBottom',
              labelKey: 'editor.toolbar.addRowAfter',
              onClick: (event) =>
                runEditorCommand(event, () => editor.chain().focus().addRowAfter().run()),
              testId: 'widgets-editor-toolbar-add-row-after',
            }),
            renderToolbarButton({
              disabled: !editor.can().chain().focus().deleteRow().run(),
              iconName: 'Rows',
              labelKey: 'editor.toolbar.deleteRow',
              onClick: (event) =>
                runEditorCommand(event, () => editor.chain().focus().deleteRow().run()),
              testId: 'widgets-editor-toolbar-delete-row',
            }),
          ])}

          {renderToolbarSection('widgets-editor-toolbar-table-cell', [
            renderToolbarButton({
              disabled: !editor.can().chain().focus().toggleHeaderRow().run(),
              iconName: 'Rows',
              labelKey: 'editor.toolbar.toggleHeaderRow',
              onClick: (event) =>
                runEditorCommand(event, () => editor.chain().focus().toggleHeaderRow().run()),
              testId: 'widgets-editor-toolbar-toggle-header-row',
            }),
            renderToolbarButton({
              disabled: !editor.can().chain().focus().toggleHeaderColumn().run(),
              iconName: 'TextColumns',
              labelKey: 'editor.toolbar.toggleHeaderColumn',
              onClick: (event) =>
                runEditorCommand(event, () => editor.chain().focus().toggleHeaderColumn().run()),
              testId: 'widgets-editor-toolbar-toggle-header-column',
            }),
            renderToolbarButton({
              disabled: !editor.can().chain().focus().mergeCells().run(),
              iconName: 'ArrowsMerge',
              labelKey: 'editor.toolbar.mergeCells',
              onClick: (event) =>
                runEditorCommand(event, () => editor.chain().focus().mergeCells().run()),
              testId: 'widgets-editor-toolbar-merge-cells',
            }),
            renderToolbarButton({
              disabled: !editor.can().chain().focus().splitCell().run(),
              iconName: 'ArrowsSplit',
              labelKey: 'editor.toolbar.splitCell',
              onClick: (event) =>
                runEditorCommand(event, () => editor.chain().focus().splitCell().run()),
              testId: 'widgets-editor-toolbar-split-cell',
            }),
          ])}
        </div>
      ) : null}
    </div>
  );
};

// ------------------------------------------------------
/**
 * @param {import('./editor').EditorProps} props
 * @returns {JSX.Element}
 */
export function Editor(props) {
  const { t } = useTranslation();
  // to allow selection of text the first time
  const [firstFocus, setFirstFocus] = useState(true);

  // if (Configs.devEnvironment) console.log ("[editor.js][default()] Initial Content: ", props.initialContent);
  // if (Configs.devEnvironment) console.log ("[editor.js][default()] Content: ", props.content);
  // if (Configs.devEnvironment) console.log ("[editor.js][default()] Placeholder: ", props.placeholder);

  // state variable to show / not-show the menu bar
  const [showMenuBar, setShowMenuBar] = useState(false);
  const [showLinkPopover, setShowLinkPopover] = useState(false);

  const handleEditorKeyDown = useCallback((view, event) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      setShowMenuBar(true);
      setShowLinkPopover(true);
      return true;
    }

    return false;
  }, []);

  const editor = useEditor(
    {
      content:
        props.initialContent !== undefined
          ? props.initialContent
          : props.content !== undefined
            ? props.content
            : null,

      editorProps: {
        attributes: {
          class:
            'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none p-4 flex-wrap ',
          ...(props.editorTestId ? { 'data-testid': props.editorTestId } : {}),
        },
        handleKeyDown: handleEditorKeyDown,
      },
      extensions: [
        ...createBaseEditorExtensions({ openLinkOnClick: false }),
        Placeholder.configure({
          placeholder:
            props.placeholder !== undefined
              ? props.placeholder
              : t('oneOnOne.meeting.content.placeholder'),
        }),
      ],
      onFocus({ editor }) {
        // Allow to select all text
        if (firstFocus) {
          editor.chain().focus().selectAll().run();
          setFirstFocus(false);
        }

        // Show menu bar when editor is focused
        setShowMenuBar(true);
      },
      onBlur({ editor }) {
        // The editor isn’t focused anymore.
        setShowLinkPopover(false);

        // Update state content depending on Editor type
        if (props.editorType === 'performanceReviewFeedback') {
          // It is the editor present in performance review feedback
          // attributes: answer, type (strength, growth), index
          props.setContent(editor.getHTML(), props.type, props.index);
        } else if (props.editorType === 'valuesSelectorWithComments') {
          // It is the editor present in performance review feedback
          // attributes: answer, type (strength, growth), index
          props.setContent(editor.getHTML(), props.index);
        } else if (props.editorType === 'valuesScoringSingleComment') {
          // It is the editor present in performance review feedback
          // attributes: answer, type (strength, growth), index
          props.setContent(editor.getHTML());
        } else {
          props.setContent(editor.getHTML());
        }
      },
    },
    [props.initialContent, props.editorTestId]
  );

  // // Set content after rendering - taking from props
  // useEffect(() => {
  //     const contentAfterLoad = (props.initialContent && !props.content ? props.initialContent : props.content);
  //     editor?.commands?.setContent(contentAfterLoad);

  // }, [props.initialContent, props.content]);

  /**
   * Focus when the user clicks empty padding/chrome around ProseMirror
   * (placeholder-only empty docs are otherwise only clickable on the text line).
   * @param {import('react').MouseEvent<HTMLDivElement>} event
   */
  const focusEditorFromShell = useCallback(
    (event) => {
      if (!editor || editor.isDestroyed) {
        return;
      }
      if (event.target.closest('[data-testid="widgets-editor-toolbar"]')) {
        return;
      }
      // Let TipTap place the caret when the click is already inside the contenteditable.
      if (event.target.closest('.ProseMirror')) {
        return;
      }
      editor.chain().focus('end').run();
    },
    [editor]
  );

  return (
    <div
      className="w-full cursor-text"
      data-testid="widgets-editor"
      onMouseDown={focusEditorFromShell}
    >
      {showMenuBar ? (
        <MenuBar
          editor={editor}
          setShowLinkPopover={setShowLinkPopover}
          showLinkPopover={showLinkPopover}
          t={t}
        />
      ) : null}
      <EditorContent
        className="mt-2 mb-2 w-full cursor-text rounded-lg bg-white text-left dark:bg-gray-900 dark:text-gray-100"
        data-testid="widgets-editor-content"
        editor={editor}
        id="editor"
      />
    </div>
  );
}

export default Editor;
